import type { ProductLlmGenerationJobRequest } from "@/types_api/sofia/product/ProductLlmGenerationJobRequest"
import type { ProductLlmGenerationJobResponse } from "@/types_api/sofia/product/ProductLlmGenerationJobResponse"
import {
  getRouteOrganizationSlug,
  getRouteWorkspaceId,
} from "@/utils/workspaceRoutes"

import {
  LLM_GENERATION_TASK_IDS,
  type LlmGenerationRunTaskRequest,
  type LlmGenerationStreamEvent,
} from "../../protocol"
import { getActiveClerkToken } from "./activeClerkToken"

export const SOFIA_LLM_GENERATION_JOBS_ENDPOINT =
  "/api/sofia/llm-generation/jobs"

const SOFIA_LLM_GENERATION_TASK_ID_SET = new Set<string>(
  Object.values(LLM_GENERATION_TASK_IDS),
)

const TERMINAL_STATUSES = new Set<ProductLlmGenerationJobResponse["status"]>([
  "succeeded",
  "failed",
])

const DEFAULT_POLL_INTERVAL_MS = 750
const DEFAULT_POLL_TIMEOUT_MS = 600_000
const AUTH_RETRY_DELAY_MS = 1_000
const AUTH_RETRY_STATUS = 401
const SOFIA_LLM_GENERATION_POLL_TIMEOUT_MESSAGE =
  "Sofia LLM generation job polling timed out"

interface SofiaLlmGenerationJobErrorResponse {
  error?: string | { message?: string }
}

interface RunSofiaLlmGenerationJobOptions {
  signal?: AbortSignal
  pollIntervalMs?: number
  maxPollDurationMs?: number
}

interface SofiaJobRouteIdentity {
  organization: string | null
  workspace: string | null
}

export class SofiaLlmGenerationJobClientError extends Error {
  readonly runId?: string
  readonly status?: number

  constructor(input: { message: string; runId?: string; status?: number }) {
    super(input.message)
    this.name = "SofiaLlmGenerationJobClientError"
    this.runId = input.runId
    this.status = input.status
  }
}

export function isSofiaLlmGenerationTaskId(taskId: string): boolean {
  return SOFIA_LLM_GENERATION_TASK_ID_SET.has(taskId)
}

async function getSofiaJobErrorMessage(
  response: Response,
  fallback: string,
): Promise<string> {
  try {
    const parsed = (await response.json()) as SofiaLlmGenerationJobErrorResponse
    if (typeof parsed.error === "string" && parsed.error.trim()) {
      return parsed.error
    }
    if (
      typeof parsed.error === "object" &&
      typeof parsed.error.message === "string" &&
      parsed.error.message
    ) {
      return parsed.error.message
    }
  } catch {
    // Fall through to the caller-provided fallback.
  }
  return fallback
}

function buildSofiaJobRequest(
  request: LlmGenerationRunTaskRequest,
): ProductLlmGenerationJobRequest {
  return {
    task_id: request.taskId,
    input: request.input,
  } as ProductLlmGenerationJobRequest
}

function getJobStatusUrl(
  runId: string,
  routeIdentity: SofiaJobRouteIdentity,
): string {
  const base = `${SOFIA_LLM_GENERATION_JOBS_ENDPOINT}/${encodeURIComponent(runId)}`
  const params = new URLSearchParams()
  if (routeIdentity.organization) {
    params.set("organization", routeIdentity.organization)
  }
  if (routeIdentity.workspace) {
    params.set("workspace", routeIdentity.workspace)
  }
  const query = params.toString()
  return query ? `${base}?${query}` : base
}

function getSofiaJobRouteIdentity(): SofiaJobRouteIdentity {
  return {
    organization: getBrowsedOrganizationSlug(),
    workspace: getBrowsedWorkspaceId(),
  }
}

function getBrowsedOrganizationSlug(): string | null {
  if (typeof window === "undefined") {
    return null
  }
  return getRouteOrganizationSlug(window.location.pathname)
}

function getBrowsedWorkspaceId(): string | null {
  if (typeof window === "undefined") {
    return null
  }
  return getRouteWorkspaceId(window.location.pathname)
}

async function waitForNextPoll(
  intervalMs: number,
  signal: AbortSignal | undefined,
): Promise<void> {
  if (signal?.aborted) {
    throw new DOMException("Aborted", "AbortError")
  }
  await new Promise<void>((resolve, reject) => {
    const handleAbort = () => {
      globalThis.clearTimeout(timeoutId)
      reject(new DOMException("Aborted", "AbortError"))
    }
    const timeoutId = globalThis.setTimeout(() => {
      signal?.removeEventListener("abort", handleAbort)
      resolve()
    }, intervalMs)
    signal?.addEventListener("abort", handleAbort, { once: true })
  })
}

async function fetchWithAuthRetry(
  input: RequestInfo | URL,
  init: RequestInit,
  signal: AbortSignal | undefined,
): Promise<Response> {
  const fetchWithToken = async () => {
    const token = await getActiveClerkToken()
    if (!token) {
      throw new SofiaLlmGenerationJobClientError({
        message: "Organization authentication token is unavailable",
        status: AUTH_RETRY_STATUS,
      })
    }
    const headers = new Headers(init.headers)
    headers.set("Authorization", `Bearer ${token}`)
    return fetch(input, { ...init, headers })
  }

  const response = await fetchWithToken()
  if (response.status !== AUTH_RETRY_STATUS) {
    return response
  }
  await waitForNextPoll(AUTH_RETRY_DELAY_MS, signal)
  return fetchWithToken()
}

async function enqueueSofiaLlmGenerationJob(
  request: LlmGenerationRunTaskRequest,
  routeIdentity: SofiaJobRouteIdentity,
  options: RunSofiaLlmGenerationJobOptions = {},
): Promise<ProductLlmGenerationJobResponse> {
  const response = await fetchWithAuthRetry(
    SOFIA_LLM_GENERATION_JOBS_ENDPOINT,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        ...buildSofiaJobRequest(request),
        ...(routeIdentity.organization
          ? { organization: routeIdentity.organization }
          : {}),
        ...(routeIdentity.workspace
          ? { workspace: routeIdentity.workspace }
          : {}),
      }),
      signal: options.signal,
    },
    options.signal,
  )

  if (!response.ok) {
    throw new SofiaLlmGenerationJobClientError({
      message: await getSofiaJobErrorMessage(
        response,
        `Sofia LLM generation request failed (${response.status})`,
      ),
      status: response.status,
    })
  }

  return (await response.json()) as ProductLlmGenerationJobResponse
}

async function pollSofiaLlmGenerationJob(
  runId: string,
  routeIdentity: SofiaJobRouteIdentity,
  options: RunSofiaLlmGenerationJobOptions = {},
): Promise<ProductLlmGenerationJobResponse> {
  const pollIntervalMs = options.pollIntervalMs ?? DEFAULT_POLL_INTERVAL_MS
  const maxPollDurationMs = options.maxPollDurationMs ?? DEFAULT_POLL_TIMEOUT_MS
  const pollStartedAt = Date.now()

  while (true) {
    if (Date.now() - pollStartedAt >= maxPollDurationMs) {
      throw new SofiaLlmGenerationJobClientError({
        message: SOFIA_LLM_GENERATION_POLL_TIMEOUT_MESSAGE,
        runId,
      })
    }

    const response = await fetchWithAuthRetry(
      getJobStatusUrl(runId, routeIdentity),
      {
        method: "GET",
        signal: options.signal,
      },
      options.signal,
    )

    if (!response.ok) {
      throw new SofiaLlmGenerationJobClientError({
        message: await getSofiaJobErrorMessage(
          response,
          `Sofia LLM generation status request failed (${response.status})`,
        ),
        runId,
        status: response.status,
      })
    }

    const job = (await response.json()) as ProductLlmGenerationJobResponse
    if (TERMINAL_STATUSES.has(job.status)) {
      return job
    }
    await waitForNextPoll(pollIntervalMs, options.signal)
  }
}

function requireSucceededJob<TOutput>(
  job: ProductLlmGenerationJobResponse,
): TOutput {
  if (job.status === "failed") {
    throw new SofiaLlmGenerationJobClientError({
      message: job.error || "Sofia LLM generation job failed",
      runId: job.run_id,
    })
  }
  if (job.status !== "succeeded") {
    throw new SofiaLlmGenerationJobClientError({
      message: `Sofia LLM generation job did not complete (${job.status})`,
      runId: job.run_id,
    })
  }
  return job.result as TOutput
}

export async function runSofiaLlmGenerationJob<TOutput = unknown>(
  request: LlmGenerationRunTaskRequest,
  options: RunSofiaLlmGenerationJobOptions = {},
): Promise<TOutput> {
  const routeIdentity = getSofiaJobRouteIdentity()
  const initialJob = await enqueueSofiaLlmGenerationJob(
    request,
    routeIdentity,
    options,
  )
  const completedJob = TERMINAL_STATUSES.has(initialJob.status)
    ? initialJob
    : await pollSofiaLlmGenerationJob(initialJob.run_id, routeIdentity, options)
  return requireSucceededJob<TOutput>(completedJob)
}

export function getSofiaLlmGenerationStreamEvents(
  result: unknown,
): LlmGenerationStreamEvent[] {
  if (typeof result !== "object" || result === null) {
    return []
  }
  const events = (result as { events?: unknown }).events
  if (!Array.isArray(events)) {
    return []
  }
  return events
    .filter(
      (event): event is { event: string; data?: unknown } =>
        typeof event === "object" &&
        event !== null &&
        typeof (event as { event?: unknown }).event === "string",
    )
    .map((event) => ({
      event: event.event,
      data: event.data,
    }))
}
